if (!customElements.get('editorial-story-carousel')) {
  class EditorialStoryCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.viewport = this.querySelector('[data-story-carousel-viewport]');
      this.track = this.querySelector('[data-story-carousel-track]');
      this.slides = [...this.querySelectorAll('[data-story-carousel-slide]')];
      this.previousButton = this.querySelector('[data-story-carousel-prev]');
      this.nextButton = this.querySelector('[data-story-carousel-next]');
      this.controls = this.querySelector('[data-story-carousel-controls]');
      this.progress = this.querySelector('[data-story-carousel-progress]');
      this.progressBar = this.querySelector('[data-story-carousel-progress-bar]');
      if (!this.viewport || !this.track || !this.slides.length) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.dragging = false;
      this.pointerId = null;
      this.scrollFrame = null;
      this.autoplayTimer = null;
      this.wheelTimer = null;
      this.autoplayDirection = 1;
      this.isInViewport = false;
      this.isPointerInteracting = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.previousButton?.addEventListener('click', () => this.scrollBySlide(-1), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.scrollBySlide(1), { signal: this.signal });
      this.viewport.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal: this.signal });
      this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.viewport.addEventListener('pointerdown', (event) => this.startDrag(event), { signal: this.signal });
      this.viewport.addEventListener('pointermove', (event) => this.drag(event), { signal: this.signal });
      this.viewport.addEventListener('pointerup', () => this.endDrag(), { signal: this.signal });
      this.viewport.addEventListener('pointercancel', () => this.endDrag(), { signal: this.signal });
      this.viewport.addEventListener('wheel', () => this.onWheel(), { passive: true, signal: this.signal });
      this.addEventListener('mouseenter', () => this.pauseAutoplay(), { signal: this.signal });
      this.addEventListener('mouseleave', () => this.scheduleAutoplay(), { signal: this.signal });
      this.addEventListener('focusin', () => this.pauseAutoplay(), { signal: this.signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.scheduleAutoplay();
      }, { signal: this.signal });
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), { signal: this.signal });
      this.reducedMotion.addEventListener('change', () => this.scheduleAutoplay(), { signal: this.signal });

      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.viewport);
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isInViewport = entry.isIntersecting;
          this.scheduleAutoplay();
        },
        { threshold: 0.25 },
      );
      this.intersectionObserver.observe(this);
      this.updateControls();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.wheelTimer) window.clearTimeout(this.wheelTimer);
      this.pauseAutoplay();
      this.initialized = false;
    }

    getStep() {
      const slide = this.slides[0];
      if (!slide) return 0;
      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    scrollBySlide(direction) {
      this.restartAutoplay();
      this.viewport.scrollBy({
        left: this.getStep() * direction,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    getAutoplayDelay() {
      const delay = Number.parseInt(this.dataset.storyCarouselAutoplayDelay, 10);
      return Number.isFinite(delay) ? Math.max(delay, 2000) : 5000;
    }

    canAutoplay() {
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      return this.dataset.storyCarouselAutoplay === 'true'
        && !this.reducedMotion.matches
        && !document.hidden
        && this.isInViewport
        && !this.isPointerInteracting
        && !this.matches(':hover')
        && !this.contains(document.activeElement)
        && maximum > 1;
    }

    pauseAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay() {
      this.pauseAutoplay();
      if (!this.canAutoplay()) return;
      this.autoplayTimer = window.setTimeout(() => this.advanceAutoplay(), this.getAutoplayDelay());
    }

    restartAutoplay() {
      this.pauseAutoplay();
      this.scheduleAutoplay();
    }

    onWheel() {
      this.pauseAutoplay();
      if (this.wheelTimer) window.clearTimeout(this.wheelTimer);
      this.wheelTimer = window.setTimeout(() => this.scheduleAutoplay(), 250);
    }

    advanceAutoplay() {
      this.autoplayTimer = null;
      if (!this.canAutoplay()) return;

      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const atStart = this.viewport.scrollLeft <= 1;
      const atEnd = this.viewport.scrollLeft >= maximum - 1;
      if (atEnd) this.autoplayDirection = -1;
      if (atStart) this.autoplayDirection = 1;

      this.viewport.scrollBy({
        left: this.getStep() * this.autoplayDirection,
        behavior: 'smooth',
      });
      this.scheduleAutoplay();
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.scrollBySlide(event.key === 'ArrowLeft' ? -1 : 1);
    }

    startDrag(event) {
      this.pauseAutoplay();
      this.isPointerInteracting = true;
      if (event.pointerType === 'touch' || event.button !== 0) return;
      this.pointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.viewport.scrollLeft;
      this.dragging = true;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add('is-dragging');
    }

    drag(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      event.preventDefault();
      this.viewport.scrollLeft = this.dragStartScrollLeft - (event.clientX - this.dragStartX);
    }

    endDrag() {
      if (this.dragging && this.pointerId !== null && this.viewport.hasPointerCapture(this.pointerId)) {
        this.viewport.releasePointerCapture(this.pointerId);
      }
      this.dragging = false;
      this.pointerId = null;
      this.isPointerInteracting = false;
      this.viewport.classList.remove('is-dragging');
      this.scheduleAutoplay();
    }

    requestUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateControls();
      });
    }

    updateControls() {
      const maximum = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const hasOverflow = maximum > 1;
      if (this.controls) this.controls.hidden = !hasOverflow;
      if (this.previousButton) this.previousButton.disabled = !hasOverflow || this.viewport.scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = !hasOverflow || this.viewport.scrollLeft >= maximum - 1;
      if (this.progress && this.progressBar) {
        const progress = hasOverflow ? this.viewport.scrollLeft / maximum : 0;
        this.progressBar.style.transform = `scaleX(${Math.max(0, Math.min(progress, 1))})`;
        this.progress.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      }
    }
  }

  customElements.define('editorial-story-carousel', EditorialStoryCarousel);
}
